import Seo from "@/components/Seo";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { M } from "@/lib/motionStatic";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, Download, X, LayoutGrid } from "lucide-react";
import { ALL_SLIDES, DECK } from "@/data/pitchContent";
import { SlideRenderer } from "@/pages/pitch/SlideRenderer";
import { SLIDE_W, SLIDE_H, DeckLogo } from "@/pages/pitch/SlideKit";

const useFitScale = (margin = 0.97) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const compute = () => {
      const { width, height } = el.getBoundingClientRect();
      setScale(Math.min(width / SLIDE_W, height / SLIDE_H) * margin);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [margin]);
  return [ref, scale];
};

export default function PitchDeck() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [isFs, setIsFs] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const rootRef = useRef(null);
  const [stageRef, scale] = useFitScale();
  const total = ALL_SLIDES.length;
  const slide = ALL_SLIDES[index];

  useEffect(() => {
    // title handled by <Seo/>
  }, []);

  const go = useCallback((d) => {
    setDir(d);
    setIndex((i) => Math.min(total - 1, Math.max(0, i + d)));
  }, [total]);

  const jump = useCallback((i) => { setDir(i > index ? 1 : -1); setIndex(i); setShowGrid(false); }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown" || e.key === " ") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); go(-1); }
      else if (e.key === "Home") setIndex(0);
      else if (e.key === "End") setIndex(total - 1);
      else if (e.key === "Escape") setShowGrid(false);
      else if (e.key.toLowerCase() === "g") setShowGrid((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, total]);

  const toggleFs = useCallback(async () => {
    try {
      if (!document.fullscreenElement) { await rootRef.current?.requestFullscreen(); setIsFs(true); }
      else { await document.exitFullscreen(); setIsFs(false); }
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -60 : 60 }),
  };

  return (
    <div ref={rootRef} data-testid="pitch-deck" className="fixed inset-0 z-[100] flex flex-col" style={{ background: DECK.grey }}>
      <Seo path="/pitch" title="WorkPay — Investor Pitch Deck" description="WorkPay investor pitch deck — the payment trust layer for service businesses. Confidential presentation." noindex />
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 h-16 shrink-0" style={{ background: "#fff", borderBottom: "1px solid rgba(11,11,15,0.08)" }}>
        <div className="flex items-center gap-4">
          <button data-testid="pitch-exit" onClick={() => navigate("/")} className="flex items-center gap-2 text-[14px] font-bold" style={{ color: DECK.muted }}>
            <X className="h-4 w-4" /> Exit
          </button>
          <span className="h-5 w-px" style={{ background: "rgba(11,11,15,0.12)" }} />
          <DeckLogo height={34} />
          <span className="text-[12px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full" style={{ background: DECK.accent, color: DECK.ink }}>Investor Deck</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="pitch-grid-btn" onClick={() => setShowGrid((v) => !v)} title="Overview (G)" className="h-10 w-10 rounded-xl flex items-center justify-center hover:bg-black/5 transition-colors" style={{ color: DECK.ink }}>
            <LayoutGrid className="h-5 w-5" />
          </button>
          <button data-testid="pitch-fullscreen-btn" onClick={toggleFs} title="Fullscreen" className="h-10 w-10 rounded-xl flex items-center justify-center hover:bg-black/5 transition-colors" style={{ color: DECK.ink }}>
            {isFs ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </button>
          <button data-testid="pitch-download-btn" onClick={() => navigate("/pitch/print")} className="flex items-center gap-2 rounded-full px-4 h-10 text-[14px] font-bold" style={{ background: DECK.ink, color: "#fff" }}>
            <Download className="h-4 w-4" /> Download PDF
          </button>
        </div>
      </div>

      {/* Stage */}
      <div ref={stageRef} className="relative flex-1 min-h-0 flex items-center justify-center p-6">
        <div style={{ width: SLIDE_W * scale, height: SLIDE_H * scale }} className="relative">
          <div
            className="absolute top-0 left-0 rounded-[1.6rem] overflow-hidden"
            style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: "top left", boxShadow: "0 40px 100px -40px rgba(11,11,15,0.4)", background: "#fff" }}
          >
            <AnimatePresence custom={dir} mode="wait">
              <M.div
                key={index}
                custom={dir}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-full"
              >
                <SlideRenderer slide={slide} index={index} total={total} />
              </M.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Side nav arrows */}
        <button data-testid="pitch-prev" onClick={() => go(-1)} disabled={index === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full flex items-center justify-center disabled:opacity-30 hover:scale-105 transition-transform"
          style={{ background: "#fff", boxShadow: "0 12px 30px -12px rgba(11,11,15,0.3)", color: DECK.ink }}>
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button data-testid="pitch-next" onClick={() => go(1)} disabled={index === total - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full flex items-center justify-center disabled:opacity-30 hover:scale-105 transition-transform"
          style={{ background: "#fff", boxShadow: "0 12px 30px -12px rgba(11,11,15,0.3)", color: DECK.ink }}>
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-between px-6 h-16 shrink-0" style={{ background: "#fff", borderTop: "1px solid rgba(11,11,15,0.08)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <span data-testid="pitch-counter" className="font-display text-[15px] font-extrabold" style={{ color: DECK.ink }}>
            {String(index + 1).padStart(2, "0")} <span style={{ color: DECK.muted }}>/ {String(total).padStart(2, "0")}</span>
          </span>
          <span className="text-[14px] font-bold truncate" style={{ color: DECK.muted }}>
            {slide.kicker || (slide.type === "cover" ? "Cover" : "")}
          </span>
        </div>
        {/* progress dots */}
        <div className="flex items-center gap-1.5 flex-1 justify-center max-w-[640px]">
          <div className="h-1.5 rounded-full flex-1 overflow-hidden" style={{ background: "rgba(11,11,15,0.1)" }}>
            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${((index + 1) / total) * 100}%`, background: DECK.accentDeep }} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="pitch-prev-bottom" onClick={() => go(-1)} disabled={index === 0} className="rounded-full px-4 h-10 text-[14px] font-bold disabled:opacity-30" style={{ border: "1px solid rgba(11,11,15,0.14)", color: DECK.ink }}>Prev</button>
          <button data-testid="pitch-next-bottom" onClick={() => go(1)} disabled={index === total - 1} className="rounded-full px-5 h-10 text-[14px] font-bold disabled:opacity-30" style={{ background: DECK.accent, color: DECK.ink }}>Next</button>
        </div>
      </div>

      {/* Overview grid */}
      {showGrid && (
        <div data-testid="pitch-grid" className="absolute inset-0 z-[110] overflow-y-auto p-8" style={{ background: DECK.grey }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-[22px] font-extrabold" style={{ color: DECK.ink }}>All slides</h3>
            <button onClick={() => setShowGrid(false)} className="h-10 w-10 rounded-xl flex items-center justify-center hover:bg-black/5" style={{ color: DECK.ink }}><X className="h-5 w-5" /></button>
          </div>
          <div className="grid grid-cols-4 gap-5">
            {ALL_SLIDES.map((sl, i) => (
              <div
                key={sl.id}
                role="button"
                tabIndex={0}
                data-testid={`pitch-grid-item-${i}`}
                onClick={() => jump(i)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); jump(i); } }}
                className="group text-left cursor-pointer outline-none"
              >
                <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: "16/9", border: i === index ? `2px solid ${DECK.accentDeep}` : "1px solid rgba(11,11,15,0.1)", boxShadow: "0 14px 36px -20px rgba(11,11,15,0.35)" }}>
                  <div className="absolute top-0 left-0" style={{ width: SLIDE_W, height: SLIDE_H, transform: "scale(0.215)", transformOrigin: "top left" }}>
                    <SlideRenderer slide={sl} index={i} total={total} showMeta={false} />
                  </div>
                </div>
                <p className="mt-2 text-[12px] font-bold" style={{ color: DECK.ink }}>{String(i + 1).padStart(2, "0")} · {sl.kicker || "Cover"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
